import { createCompaignBatches } from '@/features/material/redux/slice';
import { Modal, Table, Select, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Draggable from 'react-draggable';

const SequenceModal = ({
  visible,
  batches,
  onCancel,
  onSuccessFulSequenceUpdation
}) => {
  const [sequences, setSequences] = useState([]);
  const dispatch = useDispatch();

  // Draggable modal state — antd 4 pattern via modalRender + react-draggable.
  const [dragDisabled, setDragDisabled] = useState(true);
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    bottom: 0,
    right: 0
  });
  const draggleRef = useRef(null);

  useEffect(() => {
    if (batches && batches.length > 0) {
      setSequences(
        batches?.map((batch) => {
          return {
            batchId: batch?.id,
            sequence: null,
            pbn: batch?.product_batch_number,
            process_name:
              batch?.prod_label_process_master?.process_label_name || 'NA',
            sub_process_name:
              batch?.prod_label_sub_process?.sub_process_name || 'NA'
          };
        })
      );
    }
  }, [batches]);

  // Submit gate: every row must have a sequence chosen, and no two rows may share one.
  const allSequencesChosen =
    sequences.length > 0 && sequences.every((s) => s.sequence != null);
  const allSequencesUnique =
    new Set(sequences.map((s) => s.sequence)).size === sequences.length;
  const canSubmit = allSequencesChosen && allSequencesUnique;

  const handleChange = (value, batchId) => {
    setSequences((prev) =>
      prev?.map((item) =>
        item.batchId === batchId ? { ...item, sequence: value } : item
      )
    );
  };

  const handleUpdateSequence = async () => {
    // Check for unique sequence numbers
    const sequenceNumbers = sequences.map((seq) => seq.sequence);
    const uniqueSequences = new Set(sequenceNumbers);
    if (uniqueSequences.size !== sequenceNumbers.length) {
      message.error(
        'Few batches have the same sequence number. Please ensure all are unique.'
      );
    } else {
      const payload = {
        get_compaign_batches: sequences.map((seq) => ({
          dispensing_request_id: seq.batchId,
          compaign_sequence: seq.sequence.toString()
        }))
      };

      const result = await dispatch(createCompaignBatches(payload));

      if (result?.payload?.success) {
        /*closing this modal and open the Assay Calculation View */
        onSuccessFulSequenceUpdation(result?.payload?.data);
      }
    }
  };

  const onDragStart = (_event, uiData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) return;
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y)
    });
  };

  const options = Array.from({ length: batches.length }, (_, i) => ({
    value: i + 1,
    label: i + 1
  }));

  const columns = [
    {
      title: 'Batch',
      dataIndex: 'pbn',
      key: 'batch'
    },
    {
      title: 'Dispense Process',
      dataIndex: 'process_name',
      key: 'process_name'
    },
    {
      title: 'Sub Process',
      dataIndex: 'sub_process_name',
      key: 'sub_process_name'
    },
    {
      title: 'Sequence',
      dataIndex: 'sequence',
      key: 'sequence',
      render: (text, record) => (
        <Select
          value={record.sequence}
          placeholder="Select"
          style={{ width: 100 }}
          options={options}
          onChange={(value) => handleChange(value, record.batchId)}
        />
      )
    }
  ];

  return (
    <Modal
      title={
        <div
          style={{ width: '100%', cursor: 'move' }}
          onMouseOver={() => {
            if (dragDisabled) setDragDisabled(false);
          }}
          onMouseOut={() => setDragDisabled(true)}
          // antd 4 disables draggable on focus by default — keep it explicit so blur/focus don't toggle
          onFocus={() => {}}
          onBlur={() => {}}
        >
          Select Batch Sequence
        </div>
      }
      visible={visible}
      onOk={handleUpdateSequence}
      onCancel={onCancel}
      okText="Update Sequence"
      okButtonProps={{ disabled: !canSubmit }}
      modalRender={(modal) => (
        <Draggable
          disabled={dragDisabled}
          bounds={bounds}
          onStart={(event, uiData) => onDragStart(event, uiData)}
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
    >
      <Table
        dataSource={sequences}
        columns={columns}
        rowKey="batchId"
        pagination={false}
      />
    </Modal>
  );
};

export default SequenceModal;
